import { runAction } from 'b24network.security.flowtoken';

export class Ajax
{
	static actionPost(action: string, payload: Object): Promise
	{
		return runAction(action, payload);
	}

	static loadPhoneLoginDetails(login: string): Promise
	{
		return Ajax.actionPost('b24network.authorize.verifyLogin', { login });
	}

	/**
	 * Performs request to authenticate a user with given login and password
	 *
	 * @arg {String} login
	 * @arg {String} password
	 * @arg {Boolean} needRemember
	 * @arg {Object|null} confirmations Contains user confirmation flags with ads and webinars
	 * @arg {Object|null} captchaResult Contains CAPTCHA-specific CAPTCHA passing result
	 */
	static authenticate(
		login: string,
		password: string,
		needRemember: boolean,
		confirmations: ?Object = null,
		captchaResult: ?Object = null,
	): Promise
	{
		let payload = { login, password, remember: needRemember ? '1' : '0' };

		if (confirmations)
		{
			payload.agree_news = confirmations.areAdsConfirmed ? '1' : '0';
			payload.agree_webinar = confirmations.areWebinarsConfirmed ? '1' : '0';
		}

		if (captchaResult)
		{
			payload = { ...captchaResult, ...payload };
		}

		return Ajax.actionPost('b24network.authorize.webLogin', payload);
	}

	static reSendPasswordRestorePhoneCode(
		passRestoreDataSignature: string,
		captchaResult: Object = null,
	): Promise
	{
		let payload = { signedData: passRestoreDataSignature };

		if (captchaResult)
		{
			payload = { ...payload, ...captchaResult };
		}

		return Ajax.actionPost('b24network.phoneauth.resendCode', payload);
	}

	static waitForConfirmationCall(
		userId: string,
		phoneNumberToConfirm: string,
		scenario: string,
		confirmationCallId: ?string,
	): Promise
	{
		return Ajax.actionPost('b24network.account.waitForConfirmationCall', {
			userId,
			phoneNumberToConfirm,
			scenario,
			confirmationCallId,
		});
	}

	static verifyPasswordRestorePhoneCode(code: string, passRestoreUserSignature: string): Promise
	{
		return Ajax.actionPost('b24network.phoneauth.confirm', {
			code,
			signedData: passRestoreUserSignature,
		});
	}

	static verifyPasswordRestoreEmailCode(code: string, passRestoreUserSignature: string): Promise
	{
		return Ajax.actionPost('main.authcode.confirm', {
			code,
			signedData: passRestoreUserSignature,
		});
	}

	static reSendPasswordRestoreEmailCode(
		passRestoreUserSignature: string,
		captchaResult: Object = null,
	): Promise
	{
		let payload = {
			signedData: passRestoreUserSignature,
		};

		if (captchaResult)
		{
			payload = { ...payload, ...captchaResult };
		}

		return Ajax.actionPost('b24network.authorize.resendRestorePasswordEmailCode', payload);
	}

	static changePassword(
		login: string,
		password: string,
		userCheckWord: string = '',
		captchaResult: Object = null,
	): Promise
	{
		let payload = { password, userLogin: login, passwordConfirm: password };

		if (userCheckWord)
		{
			payload.checkWord = userCheckWord;
		}

		if (captchaResult)
		{
			payload = { ...payload, ...captchaResult };
		}

		return Ajax.actionPost('b24network.authorize.changePassword', payload);
	}

	static authenticateByQrCode(qrAuthToken: string, isRememberUser: boolean): Promise
	{
		const payload = {
			token: qrAuthToken,
			remember: isRememberUser ? 1 : 0,
		};

		return Ajax.actionPost('main.qrcodeauth.authenticate', payload);
	}

	static loadSsoPortals(login: string, enteredPortal: ?Object = null): Promise
	{
		const payload = { login, portal: enteredPortal, currentUri: window.location.href };

		return Ajax.actionPost('b24network.authorize.listSsoPortals', payload);
	}

	static resendNewAccountConfirmationEmailByLogin(login: string, userId: number): Promise
	{
		const payload = { userId, userLogin: login };

		return Ajax.actionPost('b24network.authorize.resendEmailConfirmWithPassword', payload);
	}

	static registerNewAccountByEmailWithoutPortal(login: string, consentSign: string = ''): Promise
	{
		return Ajax.actionPost('b24network.authorize.register', {
			login,
			password: '',
			name: '',
			lastName: '',
			confirmData: '',
			agree_news: '',
			agree_webinar: '',
			consentSign,
		});
	}

	static async getProfileId(portalOauthClientId: string): ?number
	{
		if (!portalOauthClientId)
		{
			return null;
		}

		const response = await Ajax.actionPost(
			'b24network.portal.getProfileId',
			{ portalOauthClientId },
		);

		return response?.data?.portalUserId ?? null;
	}
}
